import { useRef } from 'react'
import { cn } from '#app/utils/misc.tsx'

type Option = {
	id: string
	text: string
}

export function MCQExercise({
	options,
	onSelect,
	onSubmit,
	submitted,
	isCorrect,
	selectedAnswer,
	isSubmitting = false,
	isPendingResult = false,
	correctOptionId,
	hideSubmitButton = false,
}: {
	options: Option[]
	onSelect: (answer: string) => void
	onSubmit: (answer: string) => void
	submitted: boolean
	isCorrect?: boolean | null
	selectedAnswer: string
	isSubmitting?: boolean
	isPendingResult?: boolean
	correctOptionId?: string
	hideSubmitButton?: boolean
}) {
	const canSubmit = !!selectedAnswer && !submitted && !isSubmitting
	const containerRef = useRef<HTMLDivElement>(null)

	function focusOption(index: number) {
		const buttons =
			containerRef.current?.querySelectorAll<HTMLButtonElement>(
				'button[role="radio"]',
			)
		buttons?.[index]?.focus()
	}

	function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
		if (submitted) return
		if (event.key === 'Enter') {
			if (selectedAnswer) {
				event.preventDefault()
				onSubmit(selectedAnswer)
			}
			return
		}
		const isNext = event.key === 'ArrowDown' || event.key === 'ArrowRight'
		const isPrev = event.key === 'ArrowUp' || event.key === 'ArrowLeft'
		if (!isNext && !isPrev) return
		if (options.length === 0) return
		event.preventDefault()
		const currentIdx = options.findIndex(
			(option) => option.id === selectedAnswer,
		)
		const baseIdx = currentIdx === -1 ? 0 : currentIdx
		const nextIdx = isNext
			? (baseIdx + 1) % options.length
			: (baseIdx - 1 + options.length) % options.length
		const nextOption = options[nextIdx]
		if (!nextOption) return
		onSelect(nextOption.id)
		focusOption(nextIdx)
	}

	return (
		<div
			ref={containerRef}
			role="radiogroup"
			className="space-y-3"
			onKeyDown={handleKeyDown}
		>
			{options.map((option) => {
				const isSelected = selectedAnswer === option.id
				const isCorrectOption =
					(correctOptionId ?? '').toLowerCase() === option.id.toLowerCase()
				const revealCorrectOption =
					submitted && !isPendingResult && isCorrect === false && isCorrectOption
				return (
					<button
						key={option.id}
						type="button"
						role="radio"
						aria-checked={isSelected}
						data-selected={isSelected ? 'true' : undefined}
						onClick={() => {
							if (!submitted) onSelect(option.id)
						}}
						disabled={submitted}
						className={cn(
							'focus-visible:ring-ring flex w-full items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
							!submitted && 'hover:bg-accent cursor-pointer',
							revealCorrectOption && 'border-success bg-success/5',
							submitted &&
								isSelected &&
								!isPendingResult &&
								isCorrect === true &&
								'border-success bg-success/5',
							submitted &&
								isSelected &&
								!isPendingResult &&
								isCorrect === false &&
								'border-destructive bg-destructive/5',
							submitted &&
								!isSelected &&
								!isPendingResult &&
								!revealCorrectOption &&
								'opacity-60',
							submitted && 'cursor-default',
							isSelected && !submitted && 'border-primary bg-primary/5',
						)}
					>
						<span
							className={cn(
								'flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium',
								isSelected &&
									(!submitted || isPendingResult) &&
									'border-primary bg-primary text-primary-foreground',
								revealCorrectOption && 'border-success bg-success text-white',
								submitted &&
									isSelected &&
									!isPendingResult &&
									isCorrect === true &&
									'border-success bg-success text-white',
								submitted &&
									isSelected &&
									!isPendingResult &&
									isCorrect === false &&
									'border-destructive bg-destructive text-white',
							)}
						>
							{option.id.toUpperCase()}
						</span>
						<span dangerouslySetInnerHTML={{ __html: option.text }} />
					</button>
				)
			})}
			{hideSubmitButton ? null : (
				<div className={cn(submitted && !isPendingResult && 'hidden')}>
					<button
						type="button"
						onClick={() => selectedAnswer && onSubmit(selectedAnswer)}
						disabled={isPendingResult || !canSubmit}
						className={cn(
							'inline-flex h-10 items-center rounded-md px-4 text-sm font-medium transition-colors',
							isPendingResult && 'bg-primary text-primary-foreground cursor-wait',
							canSubmit
								? 'bg-primary text-primary-foreground hover:bg-primary/90'
								: 'border bg-muted/60 text-muted-foreground cursor-not-allowed',
						)}
					>
						{isSubmitting ? 'Se verifică...' : 'Verifică'}
					</button>
				</div>
			)}
		</div>
	)
}
